<template>
  <div class="content" v-if="isUserDataLoaded">
    <div class="container-fluid">
      <div class="row">
        <div class="col-md-12">
          <Card>
            <template slot="header">
              <div class="row">
                <div class="col-sm-6">
                  <h5 class="title">{{ $store.state.auth.user.username }}</h5>
                </div>
                <div class="col-sm-6 text-right" v-if="!isResignedUser">
                  <button class="applyleave" @click="openModal">
                    Apply for leave
                  </button>
                </div>
              </div>
            </template>
            <div style="margin-bottom: 20px;">
              <CardsSection :cards="cards" />
            </div>
            <div>
            <CustomTable :columns="tableColumns" :data="leaves || []" :loading="isLoading">
              <template #assignees="{ item }">
                <span class="truncate-text" :title="item.assignees">{{ item.assignees || '-' }}</span>
              </template>

              <template #type="{ item }">
                <span class="truncate-text" :title="item.type">{{ item.type || '-' }}</span>
              </template>

              <template #noofdays="{ item }">
                <span class="truncate-text" :title="item.noofdays">{{ item.noofdays || '-' }}</span>
              </template>

              <template #dates="{ item }">
                <span class="truncate-text" :title="item.dates">{{ item.dates || '-' }}</span>
              </template>

              <template #createdAt="{ item }">
                {{ formatRelativeTime(item.createdAt) }}
              </template>

              <template #reason="{ item }">
                <span class="truncate-text" :title="item.reason">{{ item.reason || '-' }}</span>
              </template>

              <template #comment="{ item }">
                <span class="truncate-text" :title="item.comment">{{ item.comment || '-' }}</span>
              </template>

              <template #status="{ item }">
                <div v-if="item.status === 'Pending'">
                  <button 
                    class="withdraw-btn" 
                    @click.stop="withdrawLeave(item)"
                  >
                    Withdraw
                  </button>
                </div>
                <span v-else :class="getStatusClass(item.status)">
                  {{ item.status }}
                </span>
              </template>
            </CustomTable>

            </div>
          </Card>
        </div>
      </div>
    </div>
    <ReuseableModal :modelValue="isModalOpen" @update:modelValue="isModalOpen = $event" title="Add Leave" :loading="loading.applyLeave"
      @submit="addLeave" submitButtonText="Submit">

      <form @submit.prevent="addLeave">
        <div>
        <CustomDropdown 
            :required="true"
            id="assignee-dropdown" label="Assignees" :options="assigneeOptions" 
            :value="selectedAssigneeName" placeholder="Select Assignee" :showError="formSubmitted && 
            !newLeave.assigneeId" errorMessage="Please select an assignee" @change="handleAssigneeChange" 
        />
        </div>
        <div>
          <CustomDropdown 
              :required="true"
              id="leave-type-dropdown" label="Leave Type" 
              :options="leaveTypeOptions" :value="newLeave.type" placeholder="Select Leave Type"
              :showError="formSubmitted && !newLeave.type" errorMessage="Please select a leave type" 
              @change="handleLeaveTypeChange" 
          />
        </div>
        <div class="form-group" style="margin-bottom: 18px;">
          <label>Duration</label>
          <div style="margin-top: 8px; display: flex; align-items: center; gap: 30px;">
            <label style="">
              <input type="radio" v-model="duration" value="full" checked> Full Day
            </label>
            <label>
              <input type="radio" v-model="duration" value="half"> Half Day
            </label>
          </div>
        </div>
        <div class="form-group row" style="margin-bottom: 13px;">
          <div class="col-md-6">
            <label for="startDate" >Start Date <span class="required-asterisk">*</span></label>
            <input type="date" id="startDate" class="form-control" v-model="newLeave.startDate" required />
          </div>
          <div class="col-md-6">
            <label for="endDate">End Date<span class="required-asterisk" style="margin-left: 3px;">*</span></label>
            <input type="date" id="endDate" class="form-control" v-model="newLeave.endDate" required />
          </div>
        </div>
        <div class="form-group">
          <label for="reason" >Reason<span class="required-asterisk" style="margin-left: 2px;">*</span></label>
          <textarea id="reason" placeholder="Type you reason..." class="form-control" v-model="newLeave.reason" required></textarea>
        </div>
      </form>
    </ReuseableModal>
    <ReuseableModal 
      :modelValue="isWithdrawModalOpen" 
      @update:modelValue="isWithdrawModalOpen = $event" 
      title="Withdraw Leave" 
      :loading="loading.withdrawLeave"
      @submit="confirmWithdraw"
      submitButtonText="Yes, Withdraw">
      <p class="m-0 p-0" >Are you sure you want to withdraw this leave? This action cannot be undone.</p>
    </ReuseableModal>

  </div>
</template>

<script>
import CustomTable from "src/components/UIComponents/CustomTable.vue";
import CardsSection from "./CardsSection.vue";
import Card from "../UIComponents/Cards/Card.vue";
import firebase from "../../assets/config/firebase";
import ReuseableModal from "../UIComponents/ReuseableModal.vue";
import CustomDropdown from '../UIComponents/CustomDropdown.vue';
import swal from "sweetalert";
import moment from 'moment';
import { calculateWorkingDays } from "src/assets/config/helpers.js"
import { 
  isHoliday, 
  calculateWorkingDaysExcludingHolidays 
} from "src/assets/config/helpers.js";
import { createActivityPayload, logAdminActivity } from "../../utils/adminLogs";
import { handleActivityNotification } from "../../services/notificationService";

export default {
  components: {
    CustomTable,
    CardsSection,
    Card,
    ReuseableModal,
    CustomDropdown, 
  },
  data() {
    return {
      isLoading: false,
      user: this.$store.state.auth.user,
      leaves: [],
      cards: [],
      loading: { applyLeave: false, responseLeaves: false, withdrawLeave: false },
      formSubmitted: false,
      duration: "full",   
      leaveTypeOptions: ["Casual Leave", "Sick Leave", "Emergency Leave"], 
      tableColumns: [
        { label: "Assignees", key: "assignees", sortable: true },
        { label: "Type", key: "type", sortable: true },
        { label: "No.of Days", key: "noofdays", sortable: true },
        { label: "Dates", key: "dates", sortable: true },
        { label: "Applied At", key: "createdAt", sortable: true },
        { label: "Reason", key: "reason" },
        { label: "Comment", key: "comment" },
        { label: "Status", key: "status"},
      ],
      isModalOpen: false,
      isWithdrawModalOpen: false,
      selectedLeaveToWithdraw: null,
      newLeave: {
        startDate: "",
        endDate: "",
        assignees: "",
        assigneeId: "",
        type: "",
        noofdays: "",
        reason: "",
        status: "Pending",
      },
      holidays: [],
    };
  },
  computed: {
    filteredUsers() {
    const company = this.$store.state.auth.company;
    const companyUserIds = company.users.map(user => user.uid);
    const filteredAdmins = this.$store.state.auth.allUsers.filter(
      user => companyUserIds.includes(user.uid) && user.role === "admin" && user.uid !== this.userId
    );
    const emailMatchUser = this.$store.state.auth.allUsers.find(
      user => user.email === company.email &&
        user.role === "admin" && user.uid !== this.userId
    );
    return emailMatchUser ? [...filteredAdmins, emailMatchUser] : filteredAdmins;
  },
    assigneeOptions() {
      return this.filteredUsers.map(user => user.username);
    },
    selectedAssigneeName() {
      if (!this.newLeave.assigneeId) return '';
      
      const selectedUser = this.filteredUsers.find(user => user.uid === this.newLeave.assigneeId);
      return selectedUser ? selectedUser.username : '';
    },
    isResignedUser() {
      const currentUser = this.$store.state.auth.user;
      return currentUser.status === "Resigned";
    },
    isUserDataLoaded() {
      return this.user && this.user.leavesAllowed !== undefined;
    },
  },
  watch: {
    "$store.state.auth.user": {
      handler(newUser) {
        this.user = newUser;
        this.updateCards();
      },
      immediate: true,
    },
  },
  methods: {
    handleAssigneeChange(selectedUsername) {
      const selectedUser = this.filteredUsers.find(user => user.username === selectedUsername);
      if (selectedUser) {
        this.newLeave.assigneeId = selectedUser.uid;
        this.newLeave.assignees = selectedUser.username;
      }
    },
    handleLeaveTypeChange(selectedType) {
      this.newLeave.type = selectedType;
    },
    
    formatRelativeTime(timestamp) {
      if (!timestamp) return 'Just now';
      return moment(timestamp).fromNow();
    },
    
    getFirstWorkingDate(start, end, workingDays) {
      let current = new Date(start);
      const endDate = new Date(end);

      while (current <= endDate) {
        const dayNum = current.getDay();
        const isWeekend = dayNum === 0 || dayNum === 6;
        const isHol = isHoliday(current, this.holidays, this.user.shift);

        if (!isWeekend && !isHol) {
          return new Date(current); 
        }
        current.setDate(current.getDate() + 1);
      }
      return null; 
    },

    updateCards() {
      const leavesBalance = parseFloat(this.user.leavesAllowed) - parseFloat(this.user.leavesTaken);
      this.cards = [
        { value: parseFloat(this.user.leavesAvailable).toFixed(1), label: "Total yearly leave(s)", color: "#007bff" },
        { value: parseFloat(this.user.leavesAllowed).toFixed(1), label: "Allowed Leave(s)", color: "#00c7c7" },
        { value: parseFloat(this.user.leavesTaken).toFixed(1), label: "Taken Leave(s)", color: "#ff4d4d" },
        { value: leavesBalance.toFixed(1), label: "Balance Leave(s)", color: "#00aaff" },
      ];
    },
    getStatusClass(status) {
      return (
        {
          "Auto-Deducted": "status-badge auto-deducted",
          "Pending": "status-badge pending",
          "Rejected": "status-badge rejected",
          "Approved": "status-badge approved",
          "Withdrawn": "status-badge withdrawn",
        }[status] || "status-badge"
      );
    },

    withdrawLeave(item) {
      this.selectedLeaveToWithdraw = item;
      this.isWithdrawModalOpen = true;
    },

    async confirmWithdraw() {
      if (!this.selectedLeaveToWithdraw) return;

      this.loading.withdrawLeave = true;

      try {
        const leave = this.selectedLeaveToWithdraw;

        const range = `${leave.startDate} to ${leave.endDate || leave.startDate}`;
        const dayText = leave.noofdays === 0.5 ? 'half day' : (leave.noofdays > 1 ? 'days' : 'day');
        const title = `${this.user.username} withdrew ${leave.type} from ${range} (${leave.noofdays} ${dayText})`;
        const description = `${this.user.email} withdrew ${leave.employee}'s ${leave.type} application from ${range} (${leave.noofdays} ${dayText}).`;

        await logAdminActivity(
          createActivityPayload({
            user: this.user,
            title,
            description,
            username: this.user.username, 
            type: "leaves", 
            uid: this.user.uid, 
            isAdmin: false
          })
        );

        await firebase.updateLeaveStatusAndComment(leave.id , "Withdrawn",this.user.uid);
        leave.status = "Withdrawn";
        this.fetchLeaves();

        swal("Success", "Leave has been withdrawn successfully.", "success");
      } catch (error) {
        console.error("Error withdrawing leave:", error);
        swal("Error", "Failed to withdraw leave. Please try again.", "error");
      } finally {
        this.loading.withdrawLeave = false;
        this.isWithdrawModalOpen = false;
        this.selectedLeaveToWithdraw = null;
      }
    },

    async fetchLeaves() {
      this.isLoading = true;

      const userId = this.$store.state.auth.user.uid;
      try {
        const userLeaves = this.$store.state.auth.userLeaves || []  
        this.leaves = userLeaves  
        this.isLoading = false;
    
      } catch (error) {
        this.isLoading = false;
      console.error("Error fetching leaves:", error);
      }
    },

    async fetchHolidays() {
      try {
        const holidaysData = await firebase.getAllHolidays();
        this.holidays = Array.isArray(holidaysData) ? holidaysData : [];
      } catch (error) {
        console.error("❌ Failed to fetch holidays:", error);
      }
    },

    checkHolidaysInRange(start, end) {
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        if (isHoliday(d, this.holidays, this.user.shift)) return true;
      }
      return false;
    },

    isRangeOnlyHolidays(start, end, workingDays) {
      let hasWorkingDay = false;
      let hasHoliday = false;

      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const isHol = isHoliday(d, this.holidays, this.user.shift);
        const isWork = this.isWorkingDayForCheck(d, workingDays);

        if (isHol) hasHoliday = true;
        if (!isHol && isWork) hasWorkingDay = true;
      }

      return hasHoliday && !hasWorkingDay;
    },

    isWorkingDayForCheck(date, workingDays) {
      const workingDaysNums = workingDays.map(day =>
        ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].indexOf(day)
      );
      return workingDaysNums.includes(date.getDay());
    },

    openModal() {
      this.formSubmitted = false; 
      this.duration = "full";
      this.isModalOpen = true;
    },
    closeModal() {
      this.isModalOpen = false;
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
    async addLeave() {
      this.formSubmitted = true; 
      if (!this.newLeave.assigneeId || !this.newLeave.type || !this.newLeave.startDate || !this.newLeave.endDate || !this.newLeave.reason) {
        swal("Error", "Please fill all required fields.", "error");
        return;
      }
      const start = new Date(this.newLeave.startDate);
      const end = this.newLeave.endDate
        ? new Date(this.newLeave.endDate)
        : new Date(this.newLeave.startDate);

      const startYear = start.getFullYear();

      const workingDays =
        this.user.workingdays ||
        (this.$store.state.auth.constants && this.$store.state.auth.constants.days) ||
        [];

      let actualWorkingDays = calculateWorkingDays(start, end, workingDays);
      const isSingleDay = this.newLeave.startDate === this.newLeave.endDate || !this.newLeave.endDate;
      if (this.duration === "half") {
        if (!isSingleDay) {
          swal("Error", "Half day is only allowed for single day leaves.", "error");
          return;
        }
        actualWorkingDays = 0.5;
      }

      if (actualWorkingDays <= 0) {
       if (this.newLeave.startDate === this.newLeave.endDate) {
          swal("Error", "Leave cannot be applied on weekend days.", "error");
        } else {
          swal("Error", "No working days found in the selected range.", "error");
        }
        return;
      }
      if (this.holidays.length === 0) {
        await this.fetchHolidays();
      }
      const hasHolidayInRange = this.checkHolidaysInRange(start, end);
      const holidayOnly = this.isRangeOnlyHolidays(start, end, workingDays);
      if (holidayOnly) {
        swal("Error", "You cannot apply leave on holidays", "error");;
        return;
      }
      if (hasHolidayInRange) {
        actualWorkingDays = calculateWorkingDaysExcludingHolidays(
          start, end, workingDays, this.holidays, this.user.shift
        );

        if (this.duration === "half") {
          actualWorkingDays = 0.5;
        }
      }

      const user = this.$store.state.auth.user;
      const employee = user.username;
      const leavesAllowed = parseFloat(user.leavesAllowed) || 0;
      const leavesTaken = parseFloat(user.leavesTaken) || 0;
      const leavesBalance = leavesAllowed - leavesTaken;

      if (leavesBalance < actualWorkingDays) {
        swal("Error", "You don't have sufficient leave balance.", "error");
        return;
      }

      const options = { day: 'numeric', month: 'short' };
      let dates;

      if (actualWorkingDays === 0.5 || actualWorkingDays === 1) {
        const firstWorking = this.getFirstWorkingDate(start, end, workingDays);
        const displayDate = firstWorking || start; 
        dates = displayDate.toLocaleDateString('en-US', options);
      } else {
        const formattedStartDate = start.toLocaleDateString('en-US', options);
        const formattedEndDate = end.toLocaleDateString('en-US', options);
        dates = `${formattedStartDate} - ${formattedEndDate}`;
      }
      const assignee = this.$store.state.auth.allUsers.find(user => user.uid === this.newLeave.assigneeId);
      try {
        this.loading.applyLeave = true;

      const payload = {
        assignees: assignee.username,
        assigneeId: this.newLeave.assigneeId,
        type: this.newLeave.type,
        noofdays: actualWorkingDays,
        reason: this.newLeave.reason,
        status: "Pending",
        userId: user.uid,
        employee,
        dates,
        startYear,
        leavesBalance,
        createdAt: Date.now(),
        startDate: this.newLeave.startDate,
        endDate: this.newLeave.endDate || this.newLeave.startDate,
        duration: this.duration
      };
      this.$store.commit("setAllLeaves", [...this.leaves, payload]);
      await firebase.submitLeave("leaves", payload);
      const { type, startDate, endDate } = this.newLeave;

      const range = actualWorkingDays > 1 ? `from ${this.formatDate(startDate)} to ${this.formatDate(endDate || startDate)}` : `on ${this.formatDate(startDate)}`;
      const { uid, username, email } = user;
      const { reason } = payload;

      let dayText = actualWorkingDays === 0.5 ? 'half day' : (actualWorkingDays > 1 ? 'days' : 'day');
      let title = `${username} applied for ${type} ${range} (${actualWorkingDays} ${dayText})`
      let description = `${email} applied for ${type} from ${range} (${actualWorkingDays} ${dayText}).`;

      await logAdminActivity(
        createActivityPayload({
          user,
          title,
          description,
          reason,
          username, 
          type: "leaves", 
          uid, 
          isAdmin: false
        })
      );
        await handleActivityNotification(
          {
            type: "leaves",                    
            activityDescription: description,
            activityName: title,  
            activityOnUser: uid,               
            activityOnUserName: username,      
            activityByUserName: username,      
          },
          this.$store.state.auth,
          uid
        );
      this.newLeave = {
        startDate: "",
        endDate: "",
        assignees: "",
        assigneeId: "",
        type: "",
        noofdays: "",
        reason: "",
        status: "Pending",
      };
      this.formSubmitted = false; 
      this.closeModal();
      this.fetchLeaves();
     swal("Success", "Leave application submitted successfully!", "success");
      } catch (error) {
        console.error("Error adding leave:", error);
        swal("Error", "Failed to submit leave application. Please try again.", "error");
      } finally {
        this.loading.applyLeave = false;
      }
    },
  },
  async mounted() {
  await Promise.all([
    this.fetchHolidays(),
    this.fetchLeaves()
  ]);
}
};
</script>

<style scoped>
/* Your styles */
</style>


// WEBPACK FOOTER //
// src/components/LeaveManagement/EmployeeLeaveManagement.vue